import { useState } from 'react';
import { Box, Flex, Link } from '@chakra-ui/react';

import { CompanyLegalBeneficiary } from '../../../../../../__generated__/GQL';
import asBentoPage from '../../../../../../common/bento/hoc/asBentoPage';
import Accordion from '../../../../../../components/_core/Accordion';
import Button from '../../../../../../components/_core/Button';
import { Checkbox } from '../../../../../../components/_core/form';
import Heading from '../../../../../../components/_core/Heading';
import SunshineIcon from '../../../../../../components/_core/SunshineIcon';
import Text from '../../../../../../components/_core/Text';
import { logEvent } from '../../../../../Analytics/analytics';
import OBNavigation from '../../../../components/OBNavigation/OBNavigation';
import { GetBeneficiariesDataQuery } from '../../graphql/queries/getBeneficiariesData.gql';
import { getCurrentUserBeneficiary } from '../ManageBeneficiariesPage/helpers';
import { getActiveSupposedBeneficiaries } from '../ManageBeneficiariesPage/storage';
import { ManageBeneficiaryViews } from '../ManageBeneficiary';
import AddBeneficiaryButton from './components/AddBeneficiaryButton';
import BeneficiaryCard from './components/BeneficiaryCard';
import locales from './locales';

interface ManageBeneficiariesProps {
  data: GetBeneficiariesDataQuery['viewer'];
  isSending: boolean;
  showSkipButton: boolean;
  onMoralBeneficiaryPress: () => void;
  onNext: () => void;
  onSkip: () => void;
  goBeneficiaryForm: (payload: {
    beneficiaryId: string | null;
    view: ManageBeneficiaryViews;
  }) => void;
}

const isCompanyLegalBeneficiaryValid = (
  beneficiary: CompanyLegalBeneficiary,
) => {
  if (beneficiary.isCurrentUser) {
    return true;
  }

  return Boolean(
    beneficiary?.firstName &&
      beneficiary?.lastName &&
      beneficiary?.gender &&
      beneficiary?.isDirector !== null &&
      beneficiary?.isMajorShareholder !== null,
  );
};

const ManageBeneficiaries = asBentoPage<ManageBeneficiariesProps>(
  ({
    data,
    goBeneficiaryForm,
    isSending,
    Layout,
    onMoralBeneficiaryPress,
    onNext,
    onPrev,
    onSkip,
    showSkipButton,
  }) => {
    const currentUserBeneficiary = getCurrentUserBeneficiary(data);
    const [confirmAddedAllBeneficiaries, setConfirmAddedAllBeneficiaries] =
      useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const supposedBeneficiaries = getActiveSupposedBeneficiaries(
      data.company.supposedBeneficiaries,
    );

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Heading marginBottom="space-40" size="lg">
          {locales.heading}
        </Heading>
        <Flex alignItems="center" marginBottom="space-16">
          <SunshineIcon color="grey.700" marginRight="space-8" name="profile" />
          <Text>{locales.accountHolder}</Text>
        </Flex>
        <Box marginBottom="space-32">
          <BeneficiaryCard
            beneficiary={currentUserBeneficiary || {}}
            isCurrentUser
          />
        </Box>
        <Flex alignItems="center">
          <SunshineIcon color="grey.700" marginRight="space-8" name="team" />
          <Text>{locales.otherBeneficiaries}</Text>
        </Flex>
        {data.company.legalBeneficiaries.map((beneficiary) =>
          beneficiary.isCurrentUser ? null : (
            <Box
              key={beneficiary.companyLegalBeneficiaryId}
              marginTop="space-16"
            >
              <BeneficiaryCard
                beneficiary={beneficiary}
                invalid={
                  !isCompanyLegalBeneficiaryValid(
                    beneficiary as CompanyLegalBeneficiary,
                  )
                }
                onClick={() => {
                  goBeneficiaryForm({
                    beneficiaryId: beneficiary.companyLegalBeneficiaryId,
                    view: ManageBeneficiaryViews.Edit,
                  });
                }}
              />
            </Box>
          ),
        )}
        {supposedBeneficiaries.map((beneficiary) => (
          <Box key={beneficiary.supposedBeneficiaryId} marginTop="space-16">
            <BeneficiaryCard
              beneficiary={beneficiary}
              invalid
              onClick={() => {
                goBeneficiaryForm({
                  beneficiaryId: beneficiary.supposedBeneficiaryId,
                  view: ManageBeneficiaryViews.Supposed,
                });
              }}
            />
          </Box>
        ))}
        <AddBeneficiaryButton
          marginBottom="space-40"
          marginTop="space-24"
          onClick={() => {
            goBeneficiaryForm({
              beneficiaryId: null,
              view: ManageBeneficiaryViews.Create,
            });
          }}
        />
        <Accordion marginBottom="space-8">
          <Accordion.Item title={locales.accordionOne.title}>
            <Accordion.Text>{locales.accordionOne.text}</Accordion.Text>
          </Accordion.Item>
        </Accordion>
        <Accordion marginBottom="space-40">
          <Accordion.Item title={locales.accordionTwo.title}>
            <Accordion.Text>
              {locales.accordionTwo.text}
              <br />
              <Link onClick={onMoralBeneficiaryPress} variant="inline">
                {locales.accordionTwo.link}
              </Link>
            </Accordion.Text>
          </Accordion.Item>
        </Accordion>
        <Box marginBottom="space-40">
          <Checkbox
            isChecked={confirmAddedAllBeneficiaries}
            onChange={(e) => {
              logEvent({
                name: 'Onboarding Beneficiary List Complete Toggled',
                properties: {
                  newDesign: true,
                  value: e.target.checked,
                },
                usePrefix: false,
              });

              setConfirmAddedAllBeneficiaries(e.target.checked);
            }}
          >
            <Text>
              {locales.formatString(locales.addedAllBeneficiaries, {
                businessName: data.company.profile.legalName,
              })}
            </Text>
          </Checkbox>
          {!confirmAddedAllBeneficiaries && hasSubmitted ? (
            <Text marginTop="space-4" size="sm" variant="danger">
              {locales.confirmAddedAllBeneficiariesError}
            </Text>
          ) : null}
        </Box>
        <Button
          isLoading={isSending}
          marginBottom="space-24"
          onClick={() => {
            if (
              confirmAddedAllBeneficiaries &&
              !supposedBeneficiaries.length &&
              data.company.legalBeneficiaries.every((beneficiary) =>
                isCompanyLegalBeneficiaryValid(
                  beneficiary as CompanyLegalBeneficiary,
                ),
              )
            ) {
              onNext();
            }

            if (!hasSubmitted) {
              setHasSubmitted(true);
            }
          }}
          width="full"
        >
          {locales.buttons.cta}
        </Button>
        {showSkipButton ? (
          <Box marginBottom="space-40" textAlign="center">
            <Button onClick={onSkip} variant="inline-secondary">
              {locales.buttons.skip}
            </Button>
          </Box>
        ) : null}
      </Layout>
    );
  },
);

export default ManageBeneficiaries;
